import { Location } from '@angular/common';
import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SidebarService } from '../sidebar/sidebar.service';
import { CommonService } from '../shared/common.service';
import { removeQueryParams } from '../utilities/url.utils';
import { EventService, EventType } from '../utilities/event.service';
import { UserService } from '../services/user.service';
import { CartButtonService } from '@app/components/cart-button/cart-button.service';
import { FilterDataComponent } from '@app/filter-data/filter-data.component';
import { ScrollDirectionService } from '@app/services/scroll-direction.service';

@Component({
    selector: 'md-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
    showBackButton = true;
    showMobileLanguagePicker = true;
    isUserLoggedIn = false;

    showFilterBar = false;
    showHeaderSearchBar = false;

    private sectionId: string;

    @ViewChild('login') login: TemplateRef<never>;
    @ViewChild('filterBar') filterBar: FilterDataComponent;
    @ViewChild('container') container: ElementRef;

    popperOptions = (options) => {
        options.onFirstUpdate = (state) => {
            if (state.elements?.arrow) {
                state.elements.arrow.style.display = 'none';
            }
        };
        return options;
    };

    private routingSubscription: Subscription;
    private userServiceSubscription: Subscription;
    private paramMapSubscription: Subscription;
    private previousScrollDirection: 'up' | 'down' = 'up';

    constructor(
        public common: CommonService,
        public cartButtonService: CartButtonService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private eventService: EventService,
        private location: Location,
        private sidebarService: SidebarService,
        private userService: UserService,
        private scrollService: ScrollDirectionService
    ) {}

    ngOnInit(): void {
        this.routingSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                let route = this.activatedRoute;
                while (route.firstChild) {
                    route = route.firstChild;
                }

                this.paramMapSubscription = route.paramMap.subscribe(
                    (params) => {
                        this.sectionId = params.get('sectionId');
                        this.handleSectionIdChange(this.sectionId);
                    }
                );
            }
        });
        this.userServiceSubscription = this.userService.isLoggedIn$.subscribe(
            ({ isLoggedIn }) => {
                this.isUserLoggedIn = isLoggedIn;
            }
        );
        this.scrollService.scrollDirection$.subscribe((dir) => {
            this.handleChangeScrollDirection(dir);
        });
    }

    private handleChangeScrollDirection(dir: 'up' | 'down'): void {
        if (dir === 'up' && dir !== this.previousScrollDirection) {
            this.container?.nativeElement?.classList.remove('hide');
        } else if (dir === 'down') {
            this.container?.nativeElement?.classList.add('hide');
        }
        this.previousScrollDirection = dir;
    }

    private handleSectionIdChange(sectionId: string): void {
        this.showBackButton = !this.isHomePage() && this.hasHistory();
        this.showFilterBar = this.isCategoryPage() || this.isSearchPage();
        this.showMobileLanguagePicker = this.isHomePage();

        if (this.showFilterBar) {
            document
                .getElementsByClassName('a-router-container')[0]
                ?.classList.add('has-filters');
        } else {
            document
                .getElementsByClassName('a-router-container')[0]
                ?.classList.remove('has-filters');
        }
    }

    toggleSearchBar() {
        this.showHeaderSearchBar = !this.showHeaderSearchBar;
    }

    isCategoryPage(): boolean {
        const url = removeQueryParams(this.router.url);
        const isGiftCardPage = url.includes('/plp/Gift%20Card/22');
        return !isGiftCardPage && url.includes('/plp/');
    }

    isSearchPage(): boolean {
        const url = removeQueryParams(this.router.url);
        return url.includes('/search');
    }

    isCTAEnabledPage(sectionId: string): boolean {
        return ['3', '8', '9'].includes(sectionId);
    }

    ngOnDestroy(): void {
        if (this.routingSubscription) {
            this.routingSubscription.unsubscribe();
        }
        this.scrollService.detach();
        if (this.userServiceSubscription) {
            this.userServiceSubscription.unsubscribe();
        }
        if (this.paramMapSubscription) {
            this.paramMapSubscription.unsubscribe();
        }
    }

    goBack() {
        this.location.back();
    }

    openSidebar() {
        this.sidebarService.toggle();
    }

    openAllFilters() {
        this.filterBar.openAllFiltersOffcanvas();
    }

    logout() {
        this.userService.setIsLoggedIn(false, {
            withMessage: true,
            notifyServerOfSignOut: true,
            isManualSignOut: true,
        });
    }

    // TODO: These will most likely need to be moved to some other place
    private hasHistory(): boolean {
        return window.history.length > 0;
    }

    isHomePage(): boolean {
        return removeQueryParams(this.router.url) === '/';
    }

    openLogin() {
        this.eventService.emitEvent(EventType.LOGIN);
    }
}
